
import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Award, Clock, TrendingUp, PlusCircle } from 'lucide-react';
import PointsIssueModal from './PointsIssueModal';
import PointsIssueHistoryModal from './PointsIssueHistoryModal';
import PointsHistoryModal from './PointsHistoryModal';
import PointsRedemptionHistoryModal from './PointsRedemptionHistoryModal';
import PointsConfigModal from './PointsConfigModal';
import { supabase } from '@/lib/supabase';
import { toast } from 'sonner';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';

export interface PointsTabProps {
  userId: string | null;
  totalPoints: number;
  userName: string;
}

// Define a proper interface for PointsConfig
export interface PointsConfig {
  id: number;
  created_at: string;
  follow_up_points: number | null;
  redemption_perc: number | null;
  user_follow_points: number | null;
  validity: number | null;
  cash_redemption_amt: number | null;
  user_id: string | null;
}

const PointsTab: React.FC<PointsTabProps> = ({ userId, totalPoints: initialTotalPoints, userName }) => {
  const [isIssueModalOpen, setIsIssueModalOpen] = useState(false);
  const [isIssueHistoryModalOpen, setIsIssueHistoryModalOpen] = useState(false);
  const [isPointsHistoryModalOpen, setIsPointsHistoryModalOpen] = useState(false);
  const [isConfigModalOpen, setIsConfigModalOpen] = useState(false);
  const [isRedemptionHistoryModalOpen, setIsRedemptionHistoryModalOpen] = useState(false);
  const [isBuyPointsModalOpen, setIsBuyPointsModalOpen] = useState(false);
  const [pointsToBuy, setPointsToBuy] = useState(10);
  const [isLoading, setIsLoading] = useState(false);
  const [totalPoints, setTotalPoints] = useState(initialTotalPoints);
  const [initialConfig, setInitialConfig] = useState<PointsConfig>({
    id: 0,
    created_at: new Date().toISOString(),
    follow_up_points: null,
    redemption_perc: null,
    user_follow_points: null,
    validity: null,
    cash_redemption_amt: null,
    user_id: null
  });
  
  // Update local totalPoints state when prop changes
  useEffect(() => {
    setTotalPoints(initialTotalPoints);
  }, [initialTotalPoints]);
  
  // Set up real-time listener for points updates
  useEffect(() => {
    if (!userId) return;
    
    // Set up realtime subscription to listen for profile updates
    const channel = supabase
      .channel('profile-updates')
      .on('postgres_changes', 
        { 
          event: 'UPDATE', 
          schema: 'public', 
          table: 'profile',
          filter: `user_id=eq.${userId}`
        }, 
        (payload) => {
          if (payload.new && typeof payload.new.total_points === 'number') {
            setTotalPoints(payload.new.total_points);
          }
        }
      )
      .subscribe();
      
    return () => {
      supabase.removeChannel(channel);
    };
  }, [userId]);
  
  useEffect(() => {
    // Check URL parameters for Stripe success or cancellation
    const urlParams = new URLSearchParams(window.location.search);
    const success = urlParams.get('success');
    const sessionId = urlParams.get('session_id');
    const points = urlParams.get('points');
    
    if (success === 'true' && sessionId && userId) {
      verifyPayment(sessionId);
      
      // Clean up URL params
      const url = new URL(window.location.href);
      url.searchParams.delete('success');
      url.searchParams.delete('session_id');
      url.searchParams.delete('points');
      window.history.replaceState({}, '', url.toString());
    }
  }, [userId]);
  
  useEffect(() => {
    // Fetch initial config when component mounts
    fetchPointsConfig();
  }, [userId]);
  
  const fetchPointsConfig = async () => {
    if (!userId) return;
    
    try {
      const { data, error } = await supabase
        .from('points_config')
        .select('*')
        .eq('user_id', userId)
        .maybeSingle();
        
      if (error) throw error;
      
      if (data) {
        setInitialConfig(data);
      }
    } catch (error) {
      console.error("Error fetching points config:", error);
    }
  };
  
  const verifyPayment = async (sessionId: string) => {
    try {
      const { data, error } = await supabase.functions.invoke('verify-payment', {
        body: { sessionId }
      });
      
      if (error) {
        toast.error('Failed to verify payment: ' + error.message);
        return;
      }
      
      if (data.success) {
        toast.success(`${data.points} points added to your account!`);
        
        // Update local points count
        if (data.points) {
          setTotalPoints(prev => prev + data.points);
          
          // Update profile table total_points
          if (userId) {
            const { error: updateError } = await supabase
              .from('profile')
              .update({ total_points: totalPoints + data.points })
              .eq('user_id', userId);
              
            if (updateError) {
              console.error("Error updating profile total points:", updateError);
            }
          }
        }
      } else {
        toast.error('Payment verification failed: ' + data.message);
      }
    } catch (error) {
      console.error('Error verifying payment:', error);
      toast.error('An error occurred while verifying your payment');
    }
  };

  const handleBuyPoints = async () => {
    if (!userId) {
      toast.error('You must be logged in to buy points');
      return;
    }
    
    if (pointsToBuy <= 0) {
      toast.error('Please enter a valid number of points');
      return;
    }
    
    setIsLoading(true);
    
    try {
      const { data, error } = await supabase.functions.invoke('create-checkout', {
        body: { amount: pointsToBuy, userId }
      });
      
      if (error) {
        toast.error('Failed to create checkout session: ' + error.message);
        return;
      }
      
      // Redirect to Stripe Checkout
      window.location.href = data.url;
    } catch (error) {
      console.error('Error creating checkout session:', error);
      toast.error('An error occurred while setting up payment');
    } finally {
      setIsLoading(false);
    }
  };

  // Handle config updates
  const handleConfigUpdated = async () => {
    await fetchPointsConfig();
    return Promise.resolve();
  };

  return (
    <div>
      <Card className="w-[400px]">
        <CardHeader>
          <CardTitle>Total Points</CardTitle>
          <CardDescription>You have accumulated points through various activities.</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="text-2xl font-bold">{totalPoints}</div>
        </CardContent>
      </Card>

      <div className="mt-6 flex justify-between">
        <Button onClick={() => setIsIssueModalOpen(true)}>
          <Award className="mr-2 h-4 w-4" />
          Issue Points
        </Button>
        <Button onClick={() => setIsBuyPointsModalOpen(true)}>
          <PlusCircle className="mr-2 h-4 w-4" />
          Buy Points
        </Button>
        <Button onClick={() => setIsConfigModalOpen(true)}>
          <TrendingUp className="mr-2 h-4 w-4" />
          Configure Points
        </Button>
      </div>

      <div className="mt-4 flex justify-between">
        <Button variant="secondary" onClick={() => setIsIssueHistoryModalOpen(true)}>
          <Clock className="mr-2 h-4 w-4" />
          Issue History
        </Button>
        <Button variant="secondary" onClick={() => setIsPointsHistoryModalOpen(true)}>
          <Clock className="mr-2 h-4 w-4" />
          Points History
        </Button>
        <Button variant="secondary" onClick={() => setIsRedemptionHistoryModalOpen(true)}>
          <Clock className="mr-2 h-4 w-4" />
          Redemption History
        </Button>
      </div>

      <PointsIssueModal open={isIssueModalOpen} onOpenChange={setIsIssueModalOpen} userId={userId} />
      <PointsIssueHistoryModal open={isIssueHistoryModalOpen} onOpenChange={setIsIssueHistoryModalOpen} userId={userId || ""} />
      <PointsHistoryModal open={isPointsHistoryModalOpen} onOpenChange={setIsPointsHistoryModalOpen} userId={userId || ""} />
      <PointsRedemptionHistoryModal open={isRedemptionHistoryModalOpen} onOpenChange={setIsRedemptionHistoryModalOpen} userId={userId || ""} />
      
      {/* Buy Points Dialog */}
      <Dialog open={isBuyPointsModalOpen} onOpenChange={setIsBuyPointsModalOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Buy Points</DialogTitle>
            <DialogDescription>
              Add points to your account. 1 point = $1 USD.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="flex flex-col gap-2">
              <label htmlFor="points-input">Points to buy</label>
              <Input
                id="points-input"
                type="number"
                min="1"
                value={pointsToBuy}
                onChange={(e) => setPointsToBuy(parseInt(e.target.value) || 0)}
              />
            </div>
            
            <div className="text-sm">
              Total cost: ${pointsToBuy}.00 USD
            </div>
            
            <Button 
              onClick={handleBuyPoints} 
              disabled={isLoading || pointsToBuy <= 0} 
              className="w-full"
            >
              {isLoading ? 'Processing...' : `Buy ${pointsToBuy} Points`}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
      
      {/* Render the PointsConfigModal only when it should be visible */}
      {isConfigModalOpen && (
        <PointsConfigModal 
          isOpen={isConfigModalOpen} 
          onClose={() => setIsConfigModalOpen(false)}
          userId={userId || ""}
          initialConfig={initialConfig}
          onConfigUpdated={handleConfigUpdated}
        />
      )}
    </div>
  );
};

export default PointsTab;
